import React, { createContext, useContext, useState, useCallback } from 'react';

export interface Bonus {
  id: string;
  employeeId: string;
  employeeName: string;
  workflowId: string;
  workflowName: string;
  amount: number;
  currency: string;
  awardedBy: string;
  awardedAt: Date;
  reason: string;
  month: string; // YYYY-MM
  year: number;
  status: 'pending' | 'approved' | 'paid';
  voucherNumber: string;
  notes?: string;
}

interface BonusContextType {
  bonuses: Bonus[];
  awardBonus: (params: {
    employeeId: string;
    employeeName: string;
    workflowId: string;
    workflowName: string;
    amount: number;
    awardedBy: string;
    reason: string;
  }) => Bonus;
  getBonusesByEmployee: (employeeId: string) => Bonus[];
  getBonusesByMonth: (month: string) => Bonus[];
  getTotalBonusForEmployee: (employeeId: string, month?: string) => number;
  markAsPaid: (bonusId: string) => void;
  getAllPendingBonuses: () => Bonus[];
}

const BonusContext = createContext<BonusContextType | undefined>(undefined);

export function BonusProvider({ children }: { children: React.ReactNode }) {
  const [bonuses, setBonuses] = useState<Bonus[]>([]);

  const awardBonus = useCallback((params: {
    employeeId: string;
    employeeName: string;
    workflowId: string;
    workflowName: string;
    amount: number;
    awardedBy: string;
    reason: string;
  }) => {
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const voucherNumber = `BON-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}-${String(bonuses.length + 1).padStart(4, '0')}`;

    const newBonus: Bonus = {
      id: `bonus_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      employeeId: params.employeeId,
      employeeName: params.employeeName,
      workflowId: params.workflowId,
      workflowName: params.workflowName,
      amount: params.amount,
      currency: 'USD',
      awardedBy: params.awardedBy,
      awardedAt: now,
      reason: params.reason,
      month,
      year: now.getFullYear(),
      status: 'approved',
      voucherNumber,
    };

    setBonuses(prev => [newBonus, ...prev]);
    return newBonus;
  }, [bonuses.length]);

  const getBonusesByEmployee = useCallback((employeeId: string) => {
    return bonuses.filter(b => b.employeeId === employeeId);
  }, [bonuses]);

  const getBonusesByMonth = useCallback((month: string) => {
    return bonuses.filter(b => b.month === month);
  }, [bonuses]);

  const getTotalBonusForEmployee = useCallback((employeeId: string, month?: string) => {
    let employeeBonuses = bonuses.filter(b => b.employeeId === employeeId);
    
    if (month) {
      employeeBonuses = employeeBonuses.filter(b => b.month === month);
    }
    
    return employeeBonuses.reduce((sum, b) => sum + b.amount, 0);
  }, [bonuses]);

  const markAsPaid = useCallback((bonusId: string) => {
    setBonuses(prev =>
      prev.map(b => b.id === bonusId ? { ...b, status: 'paid' as const } : b)
    );
  }, []);

  const getAllPendingBonuses = useCallback(() => {
    return bonuses.filter(b => b.status === 'pending' || b.status === 'approved');
  }, [bonuses]);

  return (
    <BonusContext.Provider
      value={{
        bonuses,
        awardBonus,
        getBonusesByEmployee,
        getBonusesByMonth,
        getTotalBonusForEmployee,
        markAsPaid,
        getAllPendingBonuses,
      }}
    >
      {children}
    </BonusContext.Provider>
  );
}

export function useBonus() {
  const context = useContext(BonusContext);
  if (!context) {
    throw new Error('useBonus must be used within BonusProvider');
  }
  return context;
}
